import type { ReactNode } from 'react';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '../../lib/cn';
import { Z } from '../../tokens/z-index';
import type { ZIndex } from '../../tokens/z-index';

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  delayDuration?: number;
  className?: string;
  /**
   * Override do tier de z-index do conteúdo portal-rendered. Default
   * `Z.FLOATING` (500) — já fica acima de qualquer modal (Z.MODAL = 400), então
   * tooltips dentro de um Dialog/Sheet não precisam de config. Raramente
   * necessário sobrescrever.
   */
  zIndex?: ZIndex;
}

export function TooltipProvider({ children }: { children: ReactNode }) {
  return <TooltipPrimitive.Provider delayDuration={300}>{children}</TooltipPrimitive.Provider>;
}

export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delayDuration,
  className,
  zIndex = Z.FLOATING,
}: TooltipProps) {
  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={4}
          style={{ zIndex }}
          className={cn(
            'rounded-md bg-secondary-950 px-2.5 py-1.5 text-xs text-white shadow-md',
            'animate-in fade-in-0 zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            className,
          )}
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-secondary-950" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
